import { formattedDateTime } from "@circle-react/helpers/dateTimeHelpers/formattedDateTime";
import { isPublishedPost } from "@circle-react/helpers/postHelpers";
import { userIsAdminOrModeratorOfSpace } from "@circle-react/helpers/spaceHelpers";
import { Box } from "@circleco/compass/components/Box";
import { Icon } from "@circleco/compass/components/Icon";
import { IconButton } from "@circleco/compass/components/IconButton";
import { Typography } from "@circleco/compass/components/Typography";
import { useCallback, useMemo, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";

import { t } from "@/i18n-js/instance";
import { ScheduleDateTimePickerPopover } from "@/react/components/PostsCompass/ScheduleDateTimePickerPopover";
import type { ImagePost } from "@/react/types";
import type { Space } from "@/react/types/Space";

import { parsePublishedAt } from "../helpers";
import type { ImagePostFormValues } from "../types";

interface ScheduledPostBoxProps {
  post: ImagePost;
  space: Space;
}

export const ScheduledPostBox = ({ post, space }: ScheduledPostBoxProps) => {
  const [isSchedulePickerOpen, setIsSchedulePickerOpen] = useState(false);
  const { setValue } = useFormContext<ImagePostFormValues>();
  const status = useWatch<ImagePostFormValues, "status">({ name: "status" });
  const publishedAt = useWatch<ImagePostFormValues, "published_at">({
    name: "published_at",
  });
  const scheduledAt = parsePublishedAt(publishedAt);
  const disabledDays = useMemo(() => ({ before: new Date() }), []);
  const shouldShowActions =
    userIsAdminOrModeratorOfSpace(space) && !isPublishedPost(post);

  const updatePublishedAt = useCallback(
    (date?: Date | null) => {
      setValue("published_at", date?.toISOString() ?? null, {
        shouldDirty: true,
      });
    },
    [setValue],
  );

  const onScheduleDateSave = (date: Date) => {
    updatePublishedAt(date);
    setValue("status", "scheduled", { shouldDirty: true });
  };

  const revertToDraftState = () => {
    updatePublishedAt(null);
    setValue("status", "draft", { shouldDirty: true });
  };

  if (status !== "scheduled" || !scheduledAt) {
    return null;
  }

  const formattedPublishedAt = formattedDateTime({
    dateTime: scheduledAt,
    format: "short_date_at_short_time",
  });

  return (
    <Box>
      <div className="flex flex-col gap-1">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Icon name="clock-dash" size="sm" />
            <Typography variant="label-sm" color="primary">
              {t("quick_post_v2.post_scheduled")}
            </Typography>
          </div>
          {shouldShowActions && (
            <div className="flex items-center gap-2">
              <ScheduleDateTimePickerPopover
                disabledDays={disabledDays}
                onOpenChange={setIsSchedulePickerOpen}
                onSave={onScheduleDateSave}
                open={isSchedulePickerOpen}
                side="bottom"
                trigger={
                  <IconButton
                    aria-label={t("edit")}
                    icon="edit"
                    size="sm"
                    type="button"
                    variant="ghost"
                  />
                }
                value={scheduledAt}
              />
              <IconButton
                aria-label={t("delete")}
                icon="trash-can"
                onClick={revertToDraftState}
                size="sm"
                type="button"
                variant="ghost"
              />
            </div>
          )}
        </div>
        <Typography variant="body-sm" color="primary">
          {formattedPublishedAt}
        </Typography>
      </div>
    </Box>
  );
};
